/**
 * LinkedEntryModelsList Component
 *
 * Displays a list of entry models linked to a playbook.
 * Each row shows:
 * - Entry model name
 * - Category badge (color swatch + name, or "No category" text)
 * - Archived indicator if entry model is archived
 * - Unlink button (hidden when onUnlink is undefined)
 *
 * Sorted: active items first, then alphabetically by name.
 *
 * Data is loaded once on mount and refreshed via cross-tab sync subscriptions
 * for entry_models and entry_model_categories tables.
 *
 * Note: No scroll container here — uses parent PlaybookLinkageTabs CardBody scroll.
 */

import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Pencil, Plus, X } from 'lucide-react';

import {
  Button,
  Card,
  CardBody,
  Chip,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
} from '@enfomentis/ui';
import { entryModelService } from '@/services/EntryModelService';
import { entryModelCategoryService } from '@/services/EntryModelCategoryService';
import { syncCoordinatorService } from '@/services/sync/SyncCoordinatorService';
import type { EntryModel } from '@/types/schema/EntryModel';
import type { EntryModelCategory } from '@/types/schema/EntryModelCategory';

export interface LinkedEntryModelsListProps {
  /** Array of entry model IDs linked to the playbook */
  entryModelIds: string[];
  /** Handler for unlink action - hidden when undefined */
  onUnlink?: (id: string) => void;
  /** Handler for add existing action - opens picker modal */
  onAdd?: () => void;
  /** Handler for create new action - opens create modal */
  onCreateNew?: () => void;
  /** Handler for edit action - shows Edit button when defined */
  onEdit?: (entryModel: EntryModel) => void;
}

/** Sort by status (active first) then alphabetically by name (case-insensitive) */
const sortByStatusThenName = <T extends { status: string; name: string }>(
  items: T[]
): T[] => {
  return [...items].sort((a, b) => {
    // Active items first
    if (a.status === 'active' && b.status !== 'active') return -1;
    if (a.status !== 'active' && b.status === 'active') return 1;
    // Then alphabetically by name (case-insensitive)
    return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
  });
};

export const LinkedEntryModelsList: React.FC<LinkedEntryModelsListProps> = ({
  entryModelIds,
  onUnlink,
  onAdd,
  onCreateNew,
  onEdit,
}) => {
  // Local state for cached entry models and categories
  const [allEntryModels, setAllEntryModels] = useState<EntryModel[]>([]);
  const [categories, setCategories] = useState<EntryModelCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  // Shared popover state - single source so only one menu can be open
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  /**
   * Render the Add Entry Models popover with "Create New..." and "Add Existing..." options.
   * Used in both empty state and list bottom to maintain consistency.
   */
  const renderAddPopover = (variant: 'flat' | 'light' = 'light') => {
    // Only show if at least one handler is defined
    if (!onAdd && !onCreateNew) return null;

    return (
      <Popover
        isOpen={isPopoverOpen}
        onOpenChange={setIsPopoverOpen}
        placement="bottom-start"
      >
        <PopoverTrigger>
          <Button
            size="sm"
            variant={variant}
            color={variant === 'flat' ? 'primary' : 'default'}
            startContent={<Plus size={16} />}
            aria-label="Add Entry Models"
          >
            Add Entry Models
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-1">
          <div className="flex flex-col min-w-40">
            {onCreateNew && (
              <Button
                size="md"
                variant="light"
                className="justify-start"
                onPress={() => {
                  setIsPopoverOpen(false);
                  onCreateNew();
                }}
              >
                Create New...
              </Button>
            )}
            {onAdd && (
              <Button
                size="md"
                variant="light"
                className="justify-start"
                onPress={() => {
                  setIsPopoverOpen(false);
                  onAdd();
                }}
              >
                Add Existing...
              </Button>
            )}
          </div>
        </PopoverContent>
      </Popover>
    );
  };

  // Load all entry models and categories data
  const loadData = useCallback(async () => {
    setIsLoading(true);
    setLoadError(false);
    try {
      const [entryModelData, categoryData] = await Promise.all([
        entryModelService.getAll(),
        entryModelCategoryService.getAll(),
      ]);
      setAllEntryModels(entryModelData);
      setCategories(categoryData);
    } catch (error) {
      console.error('Failed to load entry model data:', error);
      setLoadError(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load data on mount
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Subscribe to cross-tab sync for entry model changes
  useEffect(() => {
    const unsubscribe = syncCoordinatorService.subscribe('entry_models', () => {
      loadData();
    });
    return () => unsubscribe();
  }, [loadData]);

  // Subscribe to cross-tab sync for category changes
  useEffect(() => {
    const unsubscribe = syncCoordinatorService.subscribe('entry_model_categories', () => {
      loadData();
    });
    return () => unsubscribe();
  }, [loadData]);

  // Build category lookup map
  const categoryMap = useMemo(() => {
    const map = new Map<string, EntryModelCategory>();
    categories.forEach((cat) => map.set(cat.id, cat));
    return map;
  }, [categories]);

  // Filter and sort linked entry models
  const linkedEntryModels = useMemo(() => {
    const linked = allEntryModels.filter((em) => entryModelIds.includes(em.id));
    return sortByStatusThenName(linked);
  }, [allEntryModels, entryModelIds]);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8 text-text-tertiary text-sm">
        Loading...
      </div>
    );
  }

  // Error state
  if (loadError) {
    return (
      <div className="flex flex-col items-center justify-center py-8 gap-3">
        <span className="text-text-tertiary text-sm">Failed to load entry models</span>
        <Button size="sm" variant="flat" onPress={loadData}>
          Retry
        </Button>
      </div>
    );
  }

  // Empty state - shown when entryModelIds.length === 0
  if (linkedEntryModels.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 gap-4 text-center">
        <div className="flex flex-col gap-1">
          <span className="text-text-primary text-sm font-medium">No entry models linked yet</span>
          <span className="text-text-secondary text-xs max-w-sm">
            Entry models define your precise trigger and execution method — when to enter and how.
            Add entry models to clarify your decision rules.
          </span>
        </div>
        {renderAddPopover('flat')}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {/* Subtitle */}
      <p
        className="text-xs text-text-secondary truncate"
        title="Defines when and how you enter a trade once criteria are met"
      >
        Defines when and how you enter a trade once criteria are met
      </p>

      {/* List items - scroll owned by parent PlaybookLinkageTabs CardBody */}
      <div className="flex flex-col space-y-2">
        {linkedEntryModels.map((entryModel) => {
          const category = entryModel.entryModelCategoryId
            ? categoryMap.get(entryModel.entryModelCategoryId)
            : undefined;
          const isArchived = entryModel.status === 'archived';

          return (
            <Card
              key={entryModel.id}
              radius="sm"
              shadow="none"
              className="border border-default-200"
            >
              <CardBody className="p-0">
                <div
                  className={`flex items-center gap-3 p-2 ${
                    isArchived ? 'opacity-60 bg-default-50' : ''
                  }`}
                >
                  {/* Name + Description + Archived Badge */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center min-w-0">
                      <span
                        className={`font-medium truncate text-sm ${
                          isArchived ? 'text-text-tertiary' : 'text-text-primary'
                        }`}
                      >
                        {entryModel.name}
                      </span>
                      {isArchived && (
                        <Chip className="ml-2 shrink-0 text-xs text-text-tertiary bg-default-100">
                          Archived
                        </Chip>
                      )}
                    </div>
                    {entryModel.description && (
                      <span className="text-sm text-text-secondary truncate block">
                        {entryModel.description}
                      </span>
                    )}
                  </div>

                  {/* Category Badge */}
                  <div className="shrink-0">
                    {category ? (
                      <Chip
                        size="sm"
                        variant="flat"
                        className="text-xs"
                        style={{ backgroundColor: `${category.color}20`, color: category.color }}
                        startContent={
                          <span
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: category.color }}
                          />
                        }
                      >
                        {category.name}
                      </Chip>
                    ) : (
                      <span className="text-xs text-text-tertiary">No category</span>
                    )}
                  </div>

                  {/* Action cluster: Edit + Unlink */}
                  {(onEdit || onUnlink) && (
                    <div className="shrink-0 flex gap-1">
                      {onEdit && (
                        <Tooltip content="Edit Entry Model">
                          <Button
                            size="sm"
                            variant="light"
                            isIconOnly
                            aria-label={`Edit ${entryModel.name}`}
                            onPress={() => onEdit(entryModel)}
                          >
                            <Pencil size={16} />
                          </Button>
                        </Tooltip>
                      )}
                      {onUnlink && (
                        <Tooltip content="Unlink Entry Model">
                          <Button
                            size="sm"
                            variant="light"
                            isIconOnly
                            aria-label={`Unlink ${entryModel.name}`}
                            onPress={() => onUnlink(entryModel.id)}
                          >
                            <X size={16} />
                          </Button>
                        </Tooltip>
                      )}
                    </div>
                  )}
                </div>
              </CardBody>
            </Card>
          );
        })}
      </div>

      {/* Add button at bottom */}
      <div className="self-center">{renderAddPopover('light')}</div>
    </div>
  );
};
